import type { CitationEntry, CodeLibraryMunicipality, CompareResult } from "@/api/code_library_compare";
import { citationBadgeClassName } from "@/components/chat/citationMarkdown";
import { citationEntryContent, citationEntryKey } from "@/components/chat/citationUtils";

export type ComparisonSide = "home" | "comparison";

/**
 * Each community keeps one color everywhere in a comparison: the primary community uses the
 * Research Assistant citation blue, the comparison community the translation green.
 */
export const SIDE_STYLES: Record<ComparisonSide, { badge: string; chip: string }> = {
  home: {
    badge: citationBadgeClassName,
    chip: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800/60 dark:bg-blue-900/30 dark:text-blue-200",
  },
  comparison: {
    badge:
      "mx-0.5 inline-flex h-5 w-5 -translate-y-1 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-900/30 dark:text-emerald-300",
    chip: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
  },
};

export type ComparisonSource = {
  /** Shown on every badge; the same number everywhere in one comparison. */
  number: number;
  side: ComparisonSide;
  municipality: CodeLibraryMunicipality;
  label: string;
  excerpt: string;
};

/**
 * One numbering for the whole comparison: the primary community's cited sources first, then
 * the comparison community's. CodeLibrary::CompareService numbers the sources it gives the
 * summary model the same way, so a summary's [n] and an answer's badge n name the same source.
 */
export function buildComparisonSources(result: CompareResult): ComparisonSource[] {
  const sides: [ComparisonSide, CodeLibraryMunicipality, CitationEntry[]][] = [
    ["home", result.home_municipality, result.home.citations ?? []],
    ["comparison", result.comparison_municipality, result.comparison.citations ?? []],
  ];
  let number = 0;
  return sides.flatMap(([side, municipality, citations]) =>
    citations.map((entry) => {
      number += 1;
      return {
        number,
        side,
        municipality,
        label: citationEntryKey(entry) || "Unlabeled source",
        excerpt: citationEntryContent(entry),
      };
    }),
  );
}

/** Turns the summary's [n] source markers into the citation links the markdown renderer badges. */
export function linkSourceMarkers(text: string): string {
  return text.replace(/\[(\d+)\](?!\()/g, "[$1](<citation:$1>)");
}
